import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { User, LoginRequest, LogoutRequest } from '@/types'
import { authApi } from '@/services/authApi'
import { tokenStorage, getApiErrorMessage, normalizeUser } from '@/services/api'
import { AUTH_STORAGE_KEYS } from '@/types/auth'

export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  error: string | null
}

function readStoredUser(): User | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEYS.USER)
    return raw ? normalizeUser(JSON.parse(raw) as User) : null
  } catch {
    return null
  }
}

const storedUser = readStoredUser()
const hasToken = !!tokenStorage.getAccessToken()

const initialState: AuthState = {
  user: hasToken ? storedUser : null,
  isAuthenticated: hasToken && !!storedUser,
  status: 'idle',
  error: null,
}

// Login thunk — calls the real /auth/login, persists tokens, returns the user.
export const login = createAsyncThunk<
  User,
  LoginRequest,
  { rejectValue: string }
>('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const res = await authApi.login(credentials)
    if (!res.data) {
      return rejectWithValue(res.message || 'Invalid email or password.')
    }
    tokenStorage.setTokens(res.data)
    return res.data.user
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, 'Invalid email or password.'))
  }
})

// Logout thunk — invalidates server-side then clears local session.
export const logout = createAsyncThunk<void, LogoutRequest | undefined>(
  'auth/logout',
  async body => {
    try {
      await authApi.logout(body)
    } catch {
      // Even if the server call fails we still clear the client session.
    } finally {
      tokenStorage.clear()
    }
  }
)

// Refresh the authenticated user (roles + permissions) from GET /auth/me.
// Soft-fails on network errors so the cached session stays usable; 401 is
// handled by the axios interceptor (refresh / force logout).
export const fetchMe = createAsyncThunk<User, void, { rejectValue: string }>(
  'auth/fetchMe',
  async (_, { rejectWithValue }) => {
    try {
      const res = await authApi.me()
      if (!res.data) {
        return rejectWithValue(res.message || 'Failed to load profile.')
      }
      return normalizeUser(res.data)
    } catch (err) {
      return rejectWithValue(getApiErrorMessage(err, 'Failed to load profile.'))
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<User>) {
      const user = tokenStorage.setUser(action.payload)
      state.user = user
      state.isAuthenticated = true
    },
    clearError(state) {
      state.error = null
    },
    sessionExpired(state) {
      tokenStorage.clear()
      state.user = null
      state.isAuthenticated = false
      state.status = 'idle'
    },
  },
  extraReducers: builder => {
    builder
      .addCase(login.pending, state => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.user = action.payload
        state.isAuthenticated = true
        state.error = null
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload ?? 'Invalid email or password.'
        state.isAuthenticated = false
        state.user = null
      })
      .addCase(logout.fulfilled, state => {
        state.user = null
        state.isAuthenticated = false
        state.status = 'idle'
        state.error = null
      })
      .addCase(fetchMe.fulfilled, (state, action) => {
        const user = tokenStorage.setUser(action.payload)
        state.user = user
        state.isAuthenticated = true
      })
  },
})

export const { setUser, clearError, sessionExpired } = authSlice.actions
export default authSlice.reducer
